import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Calculator, ArrowRight, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';

const StickyCTA = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage];

  useEffect(() => {
    const handleScroll = () => {
      // Show after scrolling 500px and hide on contact/calculator pages
      const shouldShow = window.scrollY > 500 && 
        !location.pathname.includes('/kontakt') &&
        !location.pathname.includes('/contact') &&
        !location.pathname.includes('/calculator') &&
        !location.pathname.includes('/rechner');
      
      setIsVisible(shouldShow);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  const handleCalculatorClick = () => {
    // Track GA4 event
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'sticky_cta_calculator_click', {
        'event_category': 'engagement',
        'event_label': 'sticky_cta'
      });
    }

    // Determine which calculator to show based on current page
    if (location.pathname.includes('/seo') || location.pathname.includes('/ai-video')) {
      navigate(`/${currentLanguage}/seo-roi-rechner`);
    } else {
      navigate(`/${currentLanguage}/website-kosten-rechner`);
    }
  };

  const handleContactClick = () => {
    // Track GA4 event
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'sticky_cta_contact_click', {
        'event_category': 'engagement',
        'event_label': 'sticky_cta'
      });
    }

    navigate(`/${currentLanguage}/kontakt`);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 animate-in slide-in-from-bottom duration-300">
      <div 
        className={`transition-all duration-300 ${
          isMinimized 
            ? 'w-12 h-12' 
            : 'w-auto max-w-[350px] px-4 py-3'
        } bg-gradient-to-r from-primary via-primary/95 to-primary backdrop-blur-lg rounded-full shadow-lg shadow-primary/30 border border-primary/20`}
      >
        {isMinimized ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsMinimized(false)}
            className="w-full h-full p-0 text-primary-foreground hover:bg-transparent"
          >
            <Calculator size={20} />
          </Button>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-primary-foreground">
              <Calculator size={20} className="text-primary-foreground/90" />
              <span className="text-sm font-medium whitespace-nowrap">
                ROI berechnen
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleCalculatorClick}
                className="bg-white/20 text-primary-foreground hover:bg-white/30 border-0 px-3 py-1 text-xs font-medium"
              >
                Rechner
                <ArrowRight size={14} className="ml-1" />
              </Button>
              
              <Button
                variant="secondary"
                size="sm"
                onClick={handleContactClick}
                className="bg-white/90 text-primary hover:bg-white border-0 px-3 py-1 text-xs font-medium"
              >
                Kontakt
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMinimized(true)}
                className="p-1 text-primary-foreground/70 hover:text-primary-foreground hover:bg-white/20"
              >
                <X size={14} />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StickyCTA;